import React from 'react';

import { PassoExecutado } from '@/models/PassoExecutado';
import { ArtifactType, Solicitacao } from '@/models/Solicitacao';
import { useFeatures } from '@/features/features/providers/FeaturesProvider';
import { useEnterpriseContext } from '@/features/enterprise/contexts/EnterpriseContext';

import PrescricaoLivreService from '@/features/prescricao-livre/services/PrescricaoLivreService';
import CardSolicitacaoGroup, {
  CardSolicitacaoGroupProps,
} from '../CardSolicitacaoGroup';
import {
  SolicitacaoState,
  useSolicitacoesContext,
} from '../../providers/SolicitacoesProvider.index';
import SolicitacaoListContainer from '../SolicitacaoListContainer';

type OverridableCardGroupProps = Partial<
  Omit<
    CardSolicitacaoGroupProps,
    'items' | 'selected' | 'CardSolicitacaoComponent'
  >
>;

interface SolicitacaoListProps extends OverridableCardGroupProps {
  passo: PassoExecutado;
  tipo: ArtifactType;
  renderHeading?: () => React.ReactNode;
  CardSolicitacaoComponent: CardSolicitacaoGroupProps['CardSolicitacaoComponent'];
  children?: React.ReactNode;
}

const toSnakeCase = (value: string) => {
  return value.replace(/-/g, '_');
};

const defaultState = (solicitacao: Solicitacao): SolicitacaoState => {
  return {
    editing: false,
    saved: false,
    loading: false,
    requested: false,
    selected: false,
    source: null,
    selectable: false,
    solicitacao,
  };
};

const SolicitacaoList: React.FC<SolicitacaoListProps> = ({
  passo,
  tipo,
  renderHeading,
  CardSolicitacaoComponent,
  addConfig = {},
  groupTitle,
  groupFeedback,
  hasAddPermission,
  hasEditPermission,
  hasSelectPermission,
  hasExpandPermission,
  onItemSelect,
  onItemsSelect,
  onItemEdit: onItemEditOverride,
  onItemAdd,
  readOnly,
  children,
  ...overrides
}) => {
  const {
    list,
    selection,
    onAdd,
    change,
    destroy,
    edit,
    add,
  } = useSolicitacoesContext();
  const [{ mode }] = useEnterpriseContext();
  const { hasFeature } = useFeatures();
  const selectionBeforeEditRef = React.useRef<Record<number, boolean>>({});

  const solicitacoesState = React.useMemo(
    () => list({ tipo: tipo as ArtifactType, source: passo.id }),
    [list, passo, tipo],
  );

  const items = React.useMemo(() => {
    return solicitacoesState.map(item => item.solicitacao);
  }, [solicitacoesState]);

  const selected = React.useMemo(() => {
    return solicitacoesState
      .filter(item => item.selected)
      .map(item => item.solicitacao.id);
  }, [solicitacoesState]);

  const featurePrefix = React.useMemo(() => {
    return `solicitacoes.${toSnakeCase(tipo)}`;
  }, [tipo]);

  const autoCanSelect = React.useMemo(() => {
    return mode !== 'not_integrated';
  }, [mode]);

  const autoCanEdit = React.useMemo(() => {
    return hasFeature(`${featurePrefix}.can_edit`);
  }, [featurePrefix, hasFeature]);

  const autoCanAdd = React.useMemo(() => {
    return hasFeature(`${featurePrefix}.can_add`);
  }, [featurePrefix, hasFeature]);

  const autoReadOnly = React.useMemo(() => {
    return passo.executado;
  }, [passo.executado]);

  const generalAddConfig = React.useMemo(() => {
    return {
      title: 'Adicionar item complementar',
      onSearch:
        addConfig.onSearch ??
        ((term: string) => {
          return PrescricaoLivreService.search({ type: tipo, term });
        }),
      ...addConfig,
    };
  }, [addConfig, tipo]);

  const solicitacaoState = React.useCallback(
    (solicitacao: Solicitacao) => {
      return (
        solicitacoesState.find(
          ({ solicitacao: { id } }) => id === solicitacao.id,
        ) || defaultState(solicitacao)
      );
    },
    [solicitacoesState],
  );

  const handleItemEdit = React.useCallback(
    async (item: Solicitacao, resource: fhir4.FhirResource) => {
      if (onItemEditOverride) {
        onItemEditOverride(item, resource);
        return;
      }

      const { saved, source } = solicitacaoState(item);
      if (!source) {
        return;
      }

      if (!saved && item.solicitacao_incluida) {
        await add(item, source, resource);
        return;
      }

      await edit(item, resource);
    },
    [add, edit, onItemEditOverride, solicitacaoState],
  );

  // Empty fallback
  if (solicitacoesState.length === 0) {
    return <></>;
  }

  return (
    <SolicitacaoListContainer tipo={tipo} renderHeading={renderHeading}>
      <CardSolicitacaoGroup
        groupTitle={
          groupTitle ??
          `Selecione os itens que você quer solicitar no seu prontuário.`
        }
        groupFeedback={groupFeedback ?? 'Selecionar todos'}
        items={items}
        selected={selected}
        hasAddPermission={hasAddPermission ?? autoCanAdd}
        hasEditPermission={hasEditPermission ?? autoCanEdit}
        hasSelectPermission={hasSelectPermission ?? autoCanSelect}
        hasExpandPermission={hasExpandPermission ?? true}
        readOnly={readOnly ?? autoReadOnly}
        addConfig={generalAddConfig}
        renderGroupItem={({ solicitacao, ...rest }) => {
          const { editing = false, loading = false } =
            solicitacaoState(solicitacao) || {};

          if (CardSolicitacaoComponent) {
            return (
              <CardSolicitacaoComponent
                solicitacao={solicitacao}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...rest}
                mode={editing ? 'details' : 'default'}
                loading={loading}
                disabledMode={loading ? 'all' : rest.disabledMode}
              />
            );
          }

          return undefined;
        }}
        onItemSelect={onItemSelect ?? (item => selection([item]))}
        onItemsSelect={onItemsSelect ?? selection}
        onItemEdit={handleItemEdit}
        onItemAdd={
          onItemAdd ??
          (item => {
            onAdd(item as Solicitacao['pedido'], tipo as Solicitacao['tipo'], {
              source: passo.id,
              editing: true,
              selected: true,
            });
          })
        }
        onItemResourceEditStart={item => {
          const { selected: wasSelected } = solicitacaoState(item);

          selectionBeforeEditRef.current[item.id] = wasSelected;
          change(item, { editing: true, selected: true });
        }}
        onItemResourceEditCancel={item => {
          const previousSelected = selectionBeforeEditRef.current[item.id];
          delete selectionBeforeEditRef.current[item.id];

          const { saved } = solicitacaoState(item);

          if (!saved && item.solicitacao_incluida) {
            destroy(item);
          } else {
            change(item, {
              editing: false,
              selected: previousSelected ?? false,
            });
          }
        }}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...overrides}
      />

      {children}
    </SolicitacaoListContainer>
  );
};

export type { SolicitacaoListProps };
export default SolicitacaoList;
